import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, PageLoader } from "../../components/ui";
import FormInput from "../../components/ui/FormInput";
import { bankDetailsFields, memberBankDetailsFields } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { memberBankDetailsValidationSchema } from "../../utils/validationSchema";
import ErrorMessage from "../../components/shared/ErrorMessage";

const BankDetails = ({
  customerId,
  bankDetailsData,
  setBankDetailsData,
  handleNext,
  type,
  locked = false,
  mode,
  // The customer's own record, read ONLY for data_operator_status in onSubmit
  // below. Defaults to {} because the Customers flow does not pass it — and
  // must not be affected by the business-loan queue logic.
  personalDetailsData = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditingRaw, setIsEditingRaw] = useState(type === "Edit");
  // `locked` wins over this component's own isEditing. Passing type="View" is
  // NOT enough: the Edit button below renders *because* the type is View, and
  // clicking it sets isEditing = true, which would reopen a disbursed
  // application. Defaults to false so the Customers flow is unaffected.
  const isEditing = locked ? false : isEditingRaw;
  const setIsEditing = (v) => { if (!locked) setIsEditingRaw(v); };
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    resolver:
      mode === "businessLoan"
        ? yupResolver(memberBankDetailsValidationSchema)
        : undefined,
    defaultValues: bankDetailsData,
  });

  useEffect(() => {
    if (bankDetailsData) {
      reset(bankDetailsData);
      Object.keys(bankDetailsData).forEach((key) => {
        setValue(key, bankDetailsData[key]);
      });
    }
  }, [bankDetailsData, setValue, reset]);

  // Check if the current path is addEditBusinessLoan
  const isFromAddEditBusiness = location.pathname.toLowerCase().includes("/addeditbusinessloan");

  const onSubmit = async (data) => {
    console.log("Form Data:", JSON.stringify(data));
    setIsLoading(true);

    // Captured BEFORE the await and before setIsEditing(false) below. React
    // state is per-render so `isEditing` would still read true further down,
    // but that is a subtlety to rely on rather than state plainly.
    const wasEditing = isEditing;

    try {
      // Insert or update is decided by WHETHER THIS SECTION EXISTS, not by the
      // URL's `type` (nor by isEditing, which is just `type === "Edit"` under
      // another name). Bank Details is optional, so a customer being edited may
      // never have had a row.
      //
      // Sending type:"Edit" with no id made the server answer "Update requires a
      // valid ID." for every customer who skipped this step — 12,202 of 13,625 on
      // the live database. customerId was already being sent and did not help:
      // the Edit branch requires an id and has no fallback.
      const existingId = bankDetailsData?.id;
      const isNew = !existingId;

      const payload = {
        ...data,
        model: "bank_details",
        type: isNew ? "Add" : "Edit",
      };

      if (isNew) {
        payload.customerId = customerId;
      } else {
        payload.id = existingId;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        payload
      );

      if (response.status === 201) {
        setBankDetailsData(response.data.savedData);
        console.log("Bank Details updated successfully: ", response.data);
        setIsEditing(false);

        // Finishing the loan form moves the customer out of the Requested Loan
        // queue. Both tabs read ONE column on personal_details:
        //   Requested Loan -> data_operator_status = "pending"   (BusinessLoan.jsx:189)
        //   Verified       -> data_operator_status = "verified"  (BusinessLoan.jsx:167)
        // and until now the ONLY writer of it was the blue Verify button
        // (BusinessLoan.jsx:328). So an operator could take the pencil route,
        // complete all 11 steps, and the request would still sit in Requested
        // Loan afterwards.
        //
        // Every clause is load-bearing:
        //   mode           - the Customers flow must not touch this queue
        //   isEditing      - a real save. NOT `type === "Edit"`, which was the
        //                    first attempt and failed: `type` records how the
        //                    wizard was OPENED (the eye passes "View", the
        //                    pencil "Edit" — BusinessLoan.jsx:422-428), not what
        //                    the operator is doing now. Opening on View and
        //                    pressing "Edit Details" is a real edit, and
        //                    "Save Changes" and "Submit" are the SAME button
        //                    running this same handler (only the label differs).
        //                    isEditing also folds in `locked` (:33), so a
        //                    disbursed application can never reach here.
        //   status pending - DO NOT loosen this to a truthiness check. It is
        //                    what confines the whole rule to "a field-officer
        //                    request still sitting in Requested Loan", which is
        //                    the only case it is meant for. The column has just
        //                    TWO writers in the entire system:
        //                      updateloanrequest.js:38  -> 'pending'  (the field
        //                                                  officer's request)
        //                      BusinessLoan.jsx:328     -> 'verified' (Verify)
        //                    and the model default is the STRING "Null"
        //                    (personal_details.js:188-192), not 'pending'. So:
        //                      field officer raises request -> 'pending'   FIRES
        //                      Create Application           -> "Null"      no
        //                      already Verified             -> 'verified'  no
        //                    A customer created through Create Application can
        //                    never reach this, and neither can one opened from
        //                    the Verified tab — which also means re-opening a
        //                    verified application never drags it backwards.
        //                    The Requested Loan row and the Verified row
        //                    navigate with the SAME state shape
        //                    (BusinessLoan.jsx:417-420), so the route cannot
        //                    tell them apart. Only the status can.
        const shouldMarkVerified =
          mode === "businessLoan" &&
          wasEditing &&
          personalDetailsData?.id &&
          personalDetailsData.data_operator_status === "pending";

        if (shouldMarkVerified) {
          try {
            // Same shape as handleVerify (BusinessLoan.jsx:322-330).
            await axios.post(`${API_BASE_URL}/api/addDetails`, {
              model: "personal_details",
              type: "Edit",
              id: personalDetailsData.id,
              data_operator_status: "verified",
            });
          } catch (statusError) {
            // NOT swallowed. The bank row is already saved but the application
            // has not moved tabs, so returning to the list would hide the
            // failure behind a row sitting in the wrong queue. Stop here and
            // say so, leaving the operator able to retry.
            console.error("Error marking customer verified: ", statusError);
            setErrorMsg(
              statusError.response?.data?.error ||
              "Bank details were saved, but the application could not be moved to Verified. Please try again."
            );
            setIsLoading(false);
            return;
          }
        }

        // Navigate based on the path
        const navigateTo = isFromAddEditBusiness ? "/businessloan" : "/customers";

        setTimeout(() => {
          setIsLoading(false);
          navigate(navigateTo, {
            state: isFromAddEditBusiness ? undefined : { type: "View", customerId },
          });
        }, 1500);
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error updating bank details");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error updating Bank Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error updating bank details";
      setErrorMsg(msg);
      setIsLoading(false);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
    setIsLoading(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(bankDetailsData);
  };

  const handleContinue = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      // Navigate based on the path
      const navigateTo = isFromAddEditBusiness ? "/businessloan" : "/customers";
      navigate(navigateTo, {
        state: isFromAddEditBusiness ? undefined : { type: "View", customerId },
      });
    }, 1500);
  };

  return (
    <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden p-8 max-w-6xl mx-auto mt-6">
      {isLoading && <PageLoader message="Saving..." />}

      <div className="flex items-start gap-3.5 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
            <path d="M12 2.25a.75.75 0 00-1 0l-9.006 7a.75.75 0 00.418 1.34l.094.006h18.988a.75.75 0 00.512-1.346L12 2.25zM2.25 12a.75.75 0 00-.75.75v7.5c0 .414.336.75.75.75h19.5a.75.75 0 00.75-.75v-7.5a.75.75 0 00-.75-.75H2.25zm5.25 2.25h1.5v3.75h-1.5v-3.75zm3.75 0h1.5v3.75h-1.5v-3.75zm3.75 0h1.5v3.75h-1.5v-3.75z" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-ink-900">Bank Details</h2>
          <p className="text-xs text-ink-500 mt-0.5">Enter bank account details for verification and fund release</p>
        </div>
        {!locked && type === "View" && !isEditing && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleEditClick}
          >
            Edit Details
          </Button>
        )}
      </div>
      <ErrorMessage message={errorMsg} />
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 mb-8">
          {(mode === "customer" ? bankDetailsFields : memberBankDetailsFields).map(
            (field, index) => (
              <div key={index} className="col-span-1">
                <FormInput
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  register={register}
                  watch={watch}
                  control={control}
                  errors={errors}
                  options={field.options || []}
                  required={field.required}
                  readOnly={(type === "View" && !isEditing) || false}
                />
              </div>
            )
          )}
        </div>
        {(type !== "View" || isEditing) && (
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-border-light">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                if (isEditing) {
                  handleCancelEdit();
                } else {
                  navigate("/customers");
                }
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
            >
              {isEditing ? "Save Changes" : "Submit"}
            </Button>
          </div>
        )}
        {!locked && type === "View" && !isEditing && (
          <div className="flex justify-center gap-3 mt-8 pt-6 border-t border-border-light">
            <Button
              variant="primary"
              onClick={handleContinue}
              disabled={isLoading}
            >
              Continue
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default BankDetails;